import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { PageTitle, Stat, Badge, Card, pct } from "./_ui";

export default function GrowthMix() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["growth-mix"], queryFn: () => api.salesGrowthMix() });
  if (isLoading) return <LoadingBlock rows={5} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;

  return (
    <div data-testid="growth-mix">
      <PageTitle title="Growth Sales Mix" subtitle="Growth is ResFil's sweet spot — company target is 70% of total sales." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Company Growth Mix" value={pct(data.company_mix_percent)} tone="emerald" testid="gm-company" />
        <Stat label="Target" value={pct(data.target)} />
        <Stat label="Growth Sales" value={data.growth_sales} />
        <Stat label="Total Sales" value={data.total_sales} />
      </div>

      <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
        <span>Difference from target: <b className={data.difference_from_target >= 0 ? "text-emerald-600" : "text-red-600"}>{data.difference_from_target > 0 ? "+" : ""}{data.difference_from_target}%</b></span>
        <Badge status={data.status} />
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500" data-testid="gm-helper">
        Growth mix is a performance indicator only. Plan recommendations must continue to follow the ResFil qualification process and actual customer needs.
      </div>

      <div className="mt-6">
        <Card title="Per Representative">
          {data.reps.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No reps with sales yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400"><th className="py-2">Rep</th><th>Growth</th><th>Total</th><th>Growth Mix</th><th>Status</th></tr></thead>
              <tbody>
                {data.reps.map((r: any) => (
                  <tr key={r.user_id} className="border-t border-slate-50" data-testid={`gm-rep-${r.user_id}`}>
                    <td className="py-2 font-medium">{r.rep_name}</td><td>{r.growth}</td><td>{r.total}</td>
                    <td>{pct(r.growth_mix_percent)}</td><td><Badge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
